// Original garden hymn only. Nothing sampled.
// Warm major, clear melody — same SCORE as the rings.

import { BPM, SCORE } from "./sim.js";

const BEAT = 60 / BPM;

// Soft pad colors under each phrase (C major family with gentle wander).
const CHORDS = [
  [48, 52, 55, 60], // C
  [48, 52, 55, 60],
  [45, 48, 52, 57], // Am
  [45, 48, 52, 57],
  [43, 47, 50, 55], // G
  [43, 47, 50, 55],
  [41, 45, 48, 53], // F
  [41, 45, 48, 53],
  [48, 52, 55, 60],
  [48, 52, 55, 64],
  [47, 50, 55, 59], // G/B color
  [45, 48, 52, 57],
  [43, 47, 50, 55],
  [41, 45, 48, 53],
  [40, 43, 47, 52], // Em
  [48, 52, 55, 60],
  [48, 52, 55, 60],
  [45, 48, 52, 57],
  [43, 47, 50, 55],
  [41, 45, 48, 53],
  [48, 52, 55, 60],
  [43, 47, 50, 55],
  [48, 52, 55, 60],
  [48, 52, 55, 64],
  [43, 47, 50, 55],
  [48, 52, 55, 60, 67],
];

function midiFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function createAudio() {
  let ctx = null;
  let master = null;
  let music = null;
  let muted = false;
  let wantMute = false;
  let singing = false;
  let timer = null;
  let stepIndex = 0;
  let nextTime = 0;
  const pending = [];

  function ensure() {
    if (ctx) return ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = wantMute ? 0 : 1;
    master.connect(ctx.destination);
    music = ctx.createGain();
    music.gain.value = 1;
    const warm = ctx.createBiquadFilter();
    warm.type = "lowpass";
    warm.frequency.value = 3200;
    warm.Q.value = 0.35;
    music.connect(warm);
    warm.connect(master);

    const seconds = 2;
    const buffer = ctx.createBuffer(1, seconds * ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    const sea = ctx.createBufferSource();
    sea.buffer = buffer;
    sea.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 280;
    const seaGain = ctx.createGain();
    seaGain.gain.value = 0.07;
    sea.connect(filter);
    filter.connect(seaGain);
    seaGain.connect(master);
    sea.start();
    return ctx;
  }

  function resume() {
    const audio = ensure();
    if (!audio) return Promise.resolve();
    nextTime = Math.max(nextTime, audio.currentTime + 0.06);
    if (audio.state !== "running") return audio.resume();
    return Promise.resolve();
  }

  function holdVoyage() {
    if (ctx && ctx.state === "running") return ctx.suspend();
    return Promise.resolve();
  }

  function setMuted(next) {
    wantMute = next;
    muted = next;
    if (!master || !ctx) return;
    master.gain.setTargetAtTime(next ? 0 : 1, ctx.currentTime, 0.04);
  }

  function isMuted() {
    return wantMute;
  }

  function voice(midi, when, dur, gain, type = "sine") {
    if (!ctx || wantMute || !music) return;
    const freq = midiFreq(midi);
    const now = Math.max(when, ctx.currentTime);
    const mix = ctx.createGain();
    mix.gain.setValueAtTime(0.0001, now);
    mix.gain.exponentialRampToValueAtTime(gain, now + 0.04);
    mix.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * 0.55), now + Math.max(0.08, dur * 0.5));
    mix.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    mix.connect(music);
    for (const detune of [1, 1.004]) {
      const osc = ctx.createOscillator();
      osc.type = detune === 1 ? type : "triangle";
      osc.frequency.setValueAtTime(freq * detune, now);
      const part = ctx.createGain();
      part.gain.value = detune === 1 ? 1 : 0.18;
      osc.connect(part);
      part.connect(mix);
      osc.start(now);
      osc.stop(now + dur + 0.06);
    }
  }

  function harp(midis, when, dur, gain) {
    midis.forEach((midi, index) => {
      voice(midi, when + index * 0.028, dur, gain * (index === 0 ? 1 : 0.72), "triangle");
    });
  }

  function pad(midis, when, dur, gain) {
    midis.forEach((midi) => {
      voice(midi, when, dur, gain, "sine");
    });
  }

  function chordFor(index) {
    if (index == null || index < 0) return [48, 52, 55];
    return CHORDS[index % CHORDS.length];
  }

  function tick() {
    if (!singing || !ctx || ctx.state !== "running") return;
    const horizon = ctx.currentTime + 0.35;
    while (nextTime < horizon) {
      const note = SCORE[stepIndex % SCORE.length];
      const dur = Math.max(0.28, note.beats * BEAT * 0.92);
      const chord = chordFor(stepIndex);
      // Clear lead — the hymn should be obvious over the sea.
      voice(note.midi, nextTime, dur, 0.085, "triangle");
      voice(note.midi + 12, nextTime + 0.01, dur * 0.85, 0.028, "sine");
      // Soft bass on phrase anchors
      if (note.beats >= 1) voice(chord[0], nextTime, dur * 1.15, 0.045, "sine");
      // Pad every other step so the garden feels full without mud
      if (stepIndex % 2 === 0) pad(chord.slice(0, 3), nextTime, Math.max(dur, BEAT * 1.8), 0.018);
      stepIndex += 1;
      nextTime += note.beats * BEAT;
    }
  }

  function startVoyage() {
    const audio = ensure();
    if (!audio) return;
    singing = true;
    stepIndex = 0;
    nextTime = audio.currentTime + 0.08;
    if (!timer) timer = setInterval(tick, 40);
  }

  function stopVoyage() {
    singing = false;
  }

  function ring(grade, freq, index) {
    if (!ctx) return;
    const when = ctx.currentTime;
    const note = SCORE[(index >= 0 ? index : 0) % SCORE.length];
    const chord = chordFor(index);
    if (grade === "perfect") {
      voice(note.midi, when, 1.2, 0.1, "triangle");
      harp(chord, when, 1.7, 0.055);
    } else if (grade === "good") {
      voice(note.midi, when, 0.9, 0.075, "triangle");
      harp(chord.slice(0, 2), when, 1.1, 0.04);
    } else if (grade === "skim") {
      voice(note.midi, when, 0.55, 0.05, "triangle");
    } else {
      voice(freq && freq < 200 ? freq : 146, when, 0.32, 0.03);
    }
  }

  function arrive() {
    if (!ctx) return;
    stopVoyage();
    const when = ctx.currentTime;
    harp([60, 64, 67, 72], when, 2.6, 0.07);
    harp([60, 67, 72, 76], when + 0.85, 2.4, 0.055);
    voice(84, when + 0.2, 2.2, 0.035, "sine");
  }

  function splash() {
    if (!ctx) return;
    voice(43, ctx.currentTime, 0.22, 0.03);
  }

  function playSong(notes) {
    pending.splice(0).forEach((id) => clearTimeout(id));
    const audio = ensure();
    if (!audio) return;
    let at = 0;
    notes.forEach((note) => {
      const score = SCORE[(note.index >= 0 ? note.index : 0) % SCORE.length];
      const wait = note.grade === "miss" ? 220 : Math.round(score.beats * BEAT * 1000);
      const id = setTimeout(() => {
        if (!ctx) return;
        if (note.grade === "miss") voice(45, ctx.currentTime, 0.16, 0.02);
        else {
          const chord = chordFor(note.index);
          const gain = note.grade === "perfect" ? 0.09 : note.grade === "good" ? 0.06 : 0.04;
          voice(score.midi, ctx.currentTime, 0.75, gain, "triangle");
          if (note.grade !== "skim") harp(chord.slice(0, note.grade === "perfect" ? 3 : 2), ctx.currentTime, 0.9, gain * 0.55);
        }
      }, at);
      pending.push(id);
      at += wait;
    });
  }

  return {
    ensure,
    resume,
    holdVoyage,
    setMuted,
    isMuted,
    startVoyage,
    stopVoyage,
    ring,
    arrive,
    splash,
    playSong,
  };
}
